import type { CanvasPoint } from "./types";

export type RoofSkeletonVertex = [number, number, number];

export type RoofSkeleton = {
  vertices: RoofSkeletonVertex[];
  polygons: number[][];
};

export type RoofGeometry = {
  outlinePoints: number[];
  facePoints: number[][];
  ridgeLines: number[][];
  diagonalLines: number[][];
};

export type SinglePitchedRoofGeometry = {
  outlinePoints: number[];
  eaveLine: number[];
  colmoLine: number[];
  rakeLines: number[][];
};

export type MultiShedRoofGeometry = {
  outlinePoints: number[];
  eaveLines: number[][];
  colmoLines: number[][];
  rakeLines: number[][];
};

export type GableRoofRidgeMode = "auto" | "edge01" | "edge12";

const EPSILON = 0.0001;
const MULTI_GABLE_EXTENT = 10000;

const arePointsEqual = (pointA: CanvasPoint, pointB: CanvasPoint) =>
  Math.abs(pointA.x - pointB.x) <= EPSILON && Math.abs(pointA.y - pointB.y) <= EPSILON;

const distance = (pointA: CanvasPoint, pointB: CanvasPoint) => Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);

const getSignedArea = (ring: CanvasPoint[]) => {
  let area = 0;

  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index];
    const next = ring[(index + 1) % ring.length];
    if (!current || !next) continue;

    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
};

const getPointsFromFlatArray = (points: number[]) => {
  const vertices: CanvasPoint[] = [];

  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x === undefined || y === undefined) continue;
    const point = { x, y };

    const previousPoint = vertices[vertices.length - 1];

    if (!previousPoint || !arePointsEqual(previousPoint, point)) {
      vertices.push(point);
    }
  }

  const firstVertex = vertices[0];
  const lastVertex = vertices[vertices.length - 1];
  if (vertices.length >= 2 && firstVertex && lastVertex && arePointsEqual(firstVertex, lastVertex)) {
    vertices.pop();
  }

  return vertices;
};

const toFlatArray = (points: CanvasPoint[]) => points.flatMap((point) => [point.x, point.y]);

const closeRing = (ring: CanvasPoint[]) => (ring.length > 0 ? [...ring, { ...ring[0] }] : ring);

export const normalizeRoofOutline = (points: number[]) => {
  const ring = getPointsFromFlatArray(points);

  if (ring.length < 3) {
    return null;
  }

  const normalizedRing = getSignedArea(ring) < 0 ? [...ring].reverse() : ring;

  return {
    outlinePoints: toFlatArray(normalizedRing),
    polygon: [closeRing(normalizedRing).map((point) => [point.x, point.y])],
  };
};

const normalizeFacePoints = (polygon: number[], vertices: RoofSkeletonVertex[]) => {
  const faceVertices: CanvasPoint[] = [];

  for (const vertexIndex of polygon) {
    const vertex = vertices[vertexIndex];

    if (!vertex) continue;

    const point = {
      x: vertex[0],
      y: vertex[1],
    };

    const previousPoint = faceVertices[faceVertices.length - 1];

    if (!previousPoint || !arePointsEqual(previousPoint, point)) {
      faceVertices.push(point);
    }
  }

  const firstFaceVertex = faceVertices[0];
  const lastFaceVertex = faceVertices[faceVertices.length - 1];
  if (
    faceVertices.length >= 2 &&
    firstFaceVertex &&
    lastFaceVertex &&
    arePointsEqual(firstFaceVertex, lastFaceVertex)
  ) {
    faceVertices.pop();
  }

  if (faceVertices.length < 3 || Math.abs(getSignedArea(faceVertices)) <= EPSILON) {
    return null;
  }

  return toFlatArray(faceVertices);
};

const buildEdgeKey = (startIndex: number, endIndex: number) =>
  startIndex < endIndex ? `${startIndex}:${endIndex}` : `${endIndex}:${startIndex}`;

const mergeConnectedLineSegments = (segments: number[][]) => {
  const normalizedSegments = segments
    .map((segment) => {
      if (segment.length < 4) return null;

      return segment.slice(0, 4);
    })
    .filter((segment): segment is number[] => segment != null);

  const remaining = [...normalizedSegments];
  const merged: number[][] = [];

  while (remaining.length > 0) {
    const seed = remaining.shift();

    if (!seed) continue;

    const seedStartX = seed[0];
    const seedStartY = seed[1];
    const seedEndX = seed[2];
    const seedEndY = seed[3];

    if (
      seedStartX === undefined ||
      seedStartY === undefined ||
      seedEndX === undefined ||
      seedEndY === undefined
    ) {
      continue;
    }

    const chain: CanvasPoint[] = [
      { x: seedStartX, y: seedStartY },
      { x: seedEndX, y: seedEndY },
    ];

    let extended = true;

    while (extended) {
      extended = false;

      for (let index = 0; index < remaining.length; index += 1) {
        const candidate = remaining[index];

        if (!candidate) continue;

        const candidateStartX = candidate[0];
        const candidateStartY = candidate[1];
        const candidateEndX = candidate[2];
        const candidateEndY = candidate[3];

        if (
          candidateStartX === undefined ||
          candidateStartY === undefined ||
          candidateEndX === undefined ||
          candidateEndY === undefined
        ) {
          continue;
        }

        const start = { x: candidateStartX, y: candidateStartY };
        const end = { x: candidateEndX, y: candidateEndY };

        const first = chain[0];
        const last = chain[chain.length - 1];

        if (!first || !last) continue;

        if (arePointsEqual(last, start)) {
          chain.push(end);
          remaining.splice(index, 1);
          extended = true;
          break;
        }

        if (arePointsEqual(last, end)) {
          chain.push(start);
          remaining.splice(index, 1);
          extended = true;
          break;
        }

        if (arePointsEqual(first, end)) {
          chain.unshift(start);
          remaining.splice(index, 1);
          extended = true;
          break;
        }

        if (arePointsEqual(first, start)) {
          chain.unshift(end);
          remaining.splice(index, 1);
          extended = true;
          break;
        }
      }
    }

    const dedupedChain: CanvasPoint[] = [];

    for (const point of chain) {
      const previousPoint = dedupedChain[dedupedChain.length - 1];

      if (!previousPoint || !arePointsEqual(previousPoint, point)) {
        dedupedChain.push(point);
      }
    }

    if (dedupedChain.length >= 2) {
      merged.push(toFlatArray(dedupedChain));
    }
  }

  return merged;
};

export const buildRoofGeometry = (outlinePoints: number[], skeleton: RoofSkeleton | null): RoofGeometry | null => {
  if (!skeleton || skeleton.vertices.length === 0 || skeleton.polygons.length === 0) {
    return null;
  }

  const facePoints = skeleton.polygons
    .map((polygon) => normalizeFacePoints(polygon, skeleton.vertices))
    .filter((points): points is number[] => points != null);

  if (facePoints.length === 0) {
    return null;
  }

  const internalEdgeMap = new Map<
    string,
    {
      startIndex: number;
      endIndex: number;
      count: number;
    }
  >();

  for (const polygon of skeleton.polygons) {
    for (let index = 0; index < polygon.length; index += 1) {
      const startIndex = polygon[index];
      const endIndex = polygon[(index + 1) % polygon.length];
      if (startIndex === undefined || endIndex === undefined) continue;
      const key = buildEdgeKey(startIndex, endIndex);

      const current = internalEdgeMap.get(key);

      if (current) {
        current.count += 1;
      } else {
        internalEdgeMap.set(key, {
          startIndex,
          endIndex,
          count: 1,
        });
      }
    }
  }

  const ridgeLines: number[][] = [];
  const diagonalLines: number[][] = [];

  for (const edge of internalEdgeMap.values()) {
    if (edge.count < 2) continue;

    const startVertex = skeleton.vertices[edge.startIndex];
    const endVertex = skeleton.vertices[edge.endIndex];

    if (!startVertex || !endVertex) continue;

    const line = [startVertex[0], startVertex[1], endVertex[0], endVertex[1]];

    const startTime = startVertex[2];
    const endTime = endVertex[2];

    const isStartInterior = startTime > EPSILON;
    const isEndInterior = endTime > EPSILON;

    if (isStartInterior && isEndInterior) {
      ridgeLines.push(line);
      continue;
    }

    if (isStartInterior !== isEndInterior) {
      diagonalLines.push(line);
    }
  }

  return {
    outlinePoints,
    facePoints,
    ridgeLines: mergeConnectedLineSegments(ridgeLines),
    diagonalLines,
  };
};

export const buildGableRoofGeometry = (
  outlinePoints: number[],
  ridgeMode: GableRoofRidgeMode = "auto",
): RoofGeometry | null => {
  const ring = getPointsFromFlatArray(outlinePoints);

  if (ring.length !== 4) {
    return null;
  }

  const [p0, p1, p2, p3] = ring;

  if (!p0 || !p1 || !p2 || !p3) {
    return null;
  }

  const edge01Length = distance(p0, p1);
  const edge12Length = distance(p1, p2);

  const useEdge01 = ridgeMode === "edge01" || (ridgeMode === "auto" && edge01Length >= edge12Length);

  if (useEdge01) {
    const ridgeStart = {
      x: (p0.x + p3.x) / 2,
      y: (p0.y + p3.y) / 2,
    };

    const ridgeEnd = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };

    return {
      outlinePoints,
      facePoints: [toFlatArray([p0, p1, ridgeEnd, ridgeStart]), toFlatArray([ridgeStart, ridgeEnd, p2, p3])],
      ridgeLines: [[ridgeStart.x, ridgeStart.y, ridgeEnd.x, ridgeEnd.y]],
      diagonalLines: [],
    };
  }

  const ridgeStart = {
    x: (p0.x + p1.x) / 2,
    y: (p0.y + p1.y) / 2,
  };

  const ridgeEnd = {
    x: (p3.x + p2.x) / 2,
    y: (p3.y + p2.y) / 2,
  };

  return {
    outlinePoints,
    facePoints: [toFlatArray([p1, p2, ridgeEnd, ridgeStart]), toFlatArray([ridgeStart, ridgeEnd, p3, p0])],
    ridgeLines: [[ridgeStart.x, ridgeStart.y, ridgeEnd.x, ridgeEnd.y]],
    diagonalLines: [],
  };
};

export const buildTwoPitchRoofGeometry = (outlinePoints: number[]): RoofGeometry | null => {
  return buildGableRoofGeometry(outlinePoints, "auto");
};

export const buildCurvedRoofGeometry = (outlinePoints: number[]): RoofGeometry | null => {
  return buildGableRoofGeometry(outlinePoints, "edge12");
};

export const buildSinglePitchedRoofGeometry = (outlinePoints: number[]): SinglePitchedRoofGeometry | null => {
  const ring = getPointsFromFlatArray(outlinePoints);

  if (ring.length !== 4) {
    return null;
  }

  const p0 = ring[0];
  const p1 = ring[1];
  const p2 = ring[2];
  const p3 = ring[3];

  if (!p0 || !p1 || !p2 || !p3) {
    return null;
  }

  return {
    outlinePoints,
    eaveLine: [p0.x, p0.y, p1.x, p1.y],
    colmoLine: [p3.x, p3.y, p2.x, p2.y],
    rakeLines: [
      [p1.x, p1.y, p2.x, p2.y],
      [p3.x, p3.y, p0.x, p0.y],
    ],
  };
};

const interpolatePoint = (start: CanvasPoint, end: CanvasPoint, ratio: number): CanvasPoint => ({
  x: start.x + (end.x - start.x) * ratio,
  y: start.y + (end.y - start.y) * ratio,
});

export const buildMultiShedRoofGeometry = (
  outlinePoints: number[],
  shedCount: number,
): MultiShedRoofGeometry | null => {
  const ring = getPointsFromFlatArray(outlinePoints);
  const normalizedShedCount = Math.max(1, Math.floor(shedCount));

  if (ring.length !== 4) {
    return null;
  }

  const p0 = ring[0];
  const p1 = ring[1];
  const p2 = ring[2];
  const p3 = ring[3];

  if (!p0 || !p1 || !p2 || !p3) {
    return null;
  }

  const eaveLines: number[][] = [];
  const colmoLines: number[][] = [];
  const rakeLines: number[][] = [];

  for (let index = 0; index < normalizedShedCount; index += 1) {
    const startRatio = index / normalizedShedCount;
    const endRatio = (index + 1) / normalizedShedCount;
    const leftStart = interpolatePoint(p0, p3, startRatio);
    const rightStart = interpolatePoint(p1, p2, startRatio);
    const leftEnd = interpolatePoint(p0, p3, endRatio);
    const rightEnd = interpolatePoint(p1, p2, endRatio);

    eaveLines.push([leftStart.x, leftStart.y, rightStart.x, rightStart.y]);
    colmoLines.push([leftEnd.x, leftEnd.y, rightEnd.x, rightEnd.y]);
    rakeLines.push([rightStart.x, rightStart.y, rightEnd.x, rightEnd.y]);
    rakeLines.push([leftEnd.x, leftEnd.y, leftStart.x, leftStart.y]);
  }

  return {
    outlinePoints,
    eaveLines,
    colmoLines,
    rakeLines,
  };
};

export type MultiGableRoofLineType = "mainRidge" | "secondaryRidge" | "hip" | "valley";

export type MultiGableRoofLine = {
  id: string;
  type: MultiGableRoofLineType;
  start: CanvasPoint;
  end: CanvasPoint;
  sourceEdges?: number[];
  targetRidgeIds?: string[];
};

export type MultiGableRoofGeometry = {
  outline: CanvasPoint[];
  mainRakes: number[];
  secondaryRakes: number[];
  roofLines: MultiGableRoofLine[];
};

const midpoint = (pointA: CanvasPoint, pointB: CanvasPoint): CanvasPoint => ({
  x: (pointA.x + pointB.x) / 2,
  y: (pointA.y + pointB.y) / 2,
});

const getOutlineEdge = (points: CanvasPoint[], index: number) => ({
  start: points[index],
  end: points[(index + 1) % points.length],
});

const getEdgeMidpoint = (points: CanvasPoint[], index: number) => {
  const outlineEdge = getOutlineEdge(points, index);
  if (!outlineEdge.start || !outlineEdge.end) return null;
  return midpoint(outlineEdge.start, outlineEdge.end);
};

const getEdgeDirection = (points: CanvasPoint[], index: number): CanvasPoint | null => {
  const outlineEdge = getOutlineEdge(points, index);
  if (!outlineEdge.start || !outlineEdge.end) return null;
  return {
    x: outlineEdge.end.x - outlineEdge.start.x,
    y: outlineEdge.end.y - outlineEdge.start.y,
  };
};

const areVectorsParallel = (vectorA: CanvasPoint, vectorB: CanvasPoint) =>
  Math.abs(vectorA.x * vectorB.y - vectorA.y * vectorB.x) < EPSILON;

const getPerpendicularVector = (vector: CanvasPoint): CanvasPoint => ({
  x: -vector.y,
  y: vector.x,
});

const getPointAlongVector = (point: CanvasPoint, direction: CanvasPoint, distanceValue: number): CanvasPoint => ({
  x: point.x + direction.x * distanceValue,
  y: point.y + direction.y * distanceValue,
});

const getLineIntersection = (
  pointA: CanvasPoint,
  pointB: CanvasPoint,
  pointC: CanvasPoint,
  pointD: CanvasPoint,
): CanvasPoint | null => {
  const denominator =
    (pointA.x - pointB.x) * (pointC.y - pointD.y) - (pointA.y - pointB.y) * (pointC.x - pointD.x);

  if (Math.abs(denominator) < EPSILON) return null;

  const determinantA = pointA.x * pointB.y - pointA.y * pointB.x;
  const determinantB = pointC.x * pointD.y - pointC.y * pointD.x;

  return {
    x: (determinantA * (pointC.x - pointD.x) - (pointA.x - pointB.x) * determinantB) / denominator,
    y: (determinantA * (pointC.y - pointD.y) - (pointA.y - pointB.y) * determinantB) / denominator,
  };
};

const projectPointOnSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint): CanvasPoint => {
  const vectorX = end.x - start.x;
  const vectorY = end.y - start.y;
  const lengthSquared = vectorX * vectorX + vectorY * vectorY;

  if (lengthSquared < EPSILON) return start;

  const t = Math.max(0, Math.min(1, ((point.x - start.x) * vectorX + (point.y - start.y) * vectorY) / lengthSquared));

  return {
    x: start.x + vectorX * t,
    y: start.y + vectorY * t,
  };
};

const getCommonPointOfEdges = (points: CanvasPoint[], edgeAIndex: number, edgeBIndex: number): CanvasPoint | null => {
  const edgeA = getOutlineEdge(points, edgeAIndex);
  const edgeB = getOutlineEdge(points, edgeBIndex);
  if (!edgeA.start || !edgeA.end || !edgeB.start || !edgeB.end) return null;

  const candidatesA = [edgeA.start, edgeA.end];
  const candidatesB = [edgeB.start, edgeB.end];

  for (const pointA of candidatesA) {
    for (const pointB of candidatesB) {
      if (distance(pointA, pointB) < EPSILON) {
        return { ...pointA };
      }
    }
  }

  return getLineIntersection(edgeA.start, edgeA.end, edgeB.start, edgeB.end);
};

export const buildMainRidgesFromRakes = (points: CanvasPoint[], mainRakes: [number, number]): MultiGableRoofLine[] => {
  const [rakeA, rakeB] = mainRakes;
  const midA = getEdgeMidpoint(points, rakeA);
  const midB = getEdgeMidpoint(points, rakeB);
  const dirA = getEdgeDirection(points, rakeA);
  const dirB = getEdgeDirection(points, rakeB);

  if (!midA || !midB || !dirA || !dirB) return [];

  if (areVectorsParallel(dirA, dirB)) {
    return [
      {
        id: "main-ridge",
        type: "mainRidge",
        start: midA,
        end: midB,
        sourceEdges: [rakeA, rakeB],
      },
    ];
  }

  const perpA = getPerpendicularVector(dirA);
  const perpB = getPerpendicularVector(dirB);
  const joint = getLineIntersection(
    getPointAlongVector(midA, perpA, -MULTI_GABLE_EXTENT),
    getPointAlongVector(midA, perpA, MULTI_GABLE_EXTENT),
    getPointAlongVector(midB, perpB, -MULTI_GABLE_EXTENT),
    getPointAlongVector(midB, perpB, MULTI_GABLE_EXTENT),
  );

  if (!joint) {
    return [
      {
        id: "main-ridge",
        type: "mainRidge",
        start: midA,
        end: midB,
        sourceEdges: [rakeA, rakeB],
      },
    ];
  }

  return [
    {
      id: "main-ridge-a",
      type: "mainRidge",
      start: midA,
      end: joint,
      sourceEdges: [rakeA],
    },
    {
      id: "main-ridge-b",
      type: "mainRidge",
      start: midB,
      end: joint,
      sourceEdges: [rakeB],
    },
  ];
};

const findClosestPointOnRidge = (point: CanvasPoint, ridges: MultiGableRoofLine[]) => {
  let best: { ridge: MultiGableRoofLine; point: CanvasPoint; distance: number } | null = null;

  for (const ridge of ridges) {
    const projected = projectPointOnSegment(point, ridge.start, ridge.end);
    const projectedDistance = distance(projected, point);

    if (!best || projectedDistance < best.distance) {
      best = {
        ridge,
        point: projected,
        distance: projectedDistance,
      };
    }
  }

  return best;
};

export const buildSecondaryRidges = (
  points: CanvasPoint[],
  secondaryRakes: number[],
  mainRidges: MultiGableRoofLine[],
): MultiGableRoofLine[] => {
  const secondaryRidges: MultiGableRoofLine[] = [];

  for (const rakeIndex of secondaryRakes) {
    const start = getEdgeMidpoint(points, rakeIndex);
    if (!start) continue;
    const target = findClosestPointOnRidge(start, mainRidges);
    if (!target) continue;

    secondaryRidges.push({
        id: `secondary-ridge-${rakeIndex}`,
        type: "secondaryRidge",
        start,
        end: target.point,
        sourceEdges: [rakeIndex],
        targetRidgeIds: [target.ridge.id],
    });
  }

  return secondaryRidges;
};

export const buildHipOrValley = (
  points: CanvasPoint[],
  type: "hip" | "valley",
  eaveAIndex: number,
  eaveBIndex: number,
  ridgeA: MultiGableRoofLine,
  ridgeB: MultiGableRoofLine,
): MultiGableRoofLine | null => {
  const start = getCommonPointOfEdges(points, eaveAIndex, eaveBIndex);
  if (!start) return null;

  const end = getLineIntersection(ridgeA.start, ridgeA.end, ridgeB.start, ridgeB.end);
  if (!end) return null;

  return {
    id: `${type}-${crypto.randomUUID()}`,
    type,
    start,
    end,
    sourceEdges: [eaveAIndex, eaveBIndex],
    targetRidgeIds: [ridgeA.id, ridgeB.id],
  };
};

export const buildMultiGableRoofGeometry = (params: {
  points: CanvasPoint[];
  mainRakes: [number, number] | null;
  secondaryRakes: number[];
  hipsAndValleys?: MultiGableRoofLine[];
}): MultiGableRoofGeometry => {
  const { points, mainRakes, secondaryRakes, hipsAndValleys = [] } = params;

  if (!mainRakes) {
    return {
      outline: points,
      mainRakes: [],
      secondaryRakes,
      roofLines: [],
    };
  }

  const mainRidges = buildMainRidgesFromRakes(points, mainRakes);
  const secondaryRidges = buildSecondaryRidges(points, secondaryRakes, mainRidges);

  return {
    outline: points,
    mainRakes,
    secondaryRakes,
    roofLines: [...mainRidges, ...secondaryRidges, ...hipsAndValleys],
  };
};
